import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil } from 'rxjs';
import { CANCEL_INVOICE, CHECK_ACTIVE } from 'src/app/constants/endpoint.const';
import { BalanceService } from 'src/app/services/api/balance.service';
import { BankService } from 'src/app/services/api/bank.service';
import { BalanceResponseType } from 'src/app/types/response/balance-response.type';
import { PossibleIncomeResponseType } from 'src/app/types/response/possible-income-response.type';
import { ModalCheckPaymentComponent } from '../modal-check-payment/modal-check-payment.component';
import { ModalTopUpSaldoComponent } from '../modal-top-up-balance/modal-top-up-balance.component';
import { BaseService } from 'src/app/services/base.service';
import { BankModel } from 'src/app/models/bank.model';
import { BuatPinComponent } from 'src/app/pages/dashboard/components/modal-buat-pin/buat-pin.component';
import { BuatRekeningComponent } from 'src/app/pages/dashboard/components/modal-buat-rekening/buat-rek.component';
import { MaintenanceComponent } from '../maintenance/maintenance.component';
import * as moment from 'moment';
import { ModalTarikSaldoComponent } from 'src/app/pages/dashboard/components/modal-tarik-saldo/modal-tarik-saldo.component';
import { BlokReqWDComponent } from '../modal-blocked-request-wd/blocked-req-wd.component';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { ActiveBillResponse, wdListModel } from 'src/app/models/withdrawl-list.model';
import { ProfileService } from 'src/app/services/profile.service';
import { Profile } from 'src/app/models/profile.model';
import { ModeSwitchService } from 'src/app/services/mode-switch.service';

@Component({
  selector: 'app-withdrawl-card',
  standalone: false,
  templateUrl: './withdrawl-card.component.html',
  styleUrl: './withdrawl-card.component.scss'
})
export class WithdrawlCardComponent implements OnInit, OnDestroy {
  @Input() wdList: wdListModel[] = [];

  destroy$: Subject<void> = new Subject<void>();
  loading: boolean = true;
  balance: BalanceResponseType = { balance: 0, commission: 0 };
  possibleIncome: PossibleIncomeResponseType = { possible_income: 0 };
  pinStatus: boolean = false;
  bankList: BankModel[] = [];
  get isSandBoxMode(): boolean {
    return this.modeSwitchService.isSandboxMode();
  }
  packageStatus: number = 0;
  profile!: Profile;

  constructor(
    private balanceService: BalanceService,
    private modalService: NgbModal,
    public baseService: BaseService,
    private message: NzMessageService,
    private bankService: BankService,
    private profileService: ProfileService,
    private modeSwitchService: ModeSwitchService,
  ) {}

  ngOnInit(): void {
    this.getProfile();
    setTimeout(() => this.loading = false, 1000);
  }

  getProfile(): void {
    this.packageStatus = this.profileService.getRoLevel();
    this.profileService.profileData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.profile = data;
          this.packageStatus = data.rajaongkir_level ?? 0;
          this.pinStatus = data.pin_status ?? false;
          this.getBalance();
          this.getBankList();
        }
      });
  }

  isWithdrawalDisabled(): boolean {
    return this.isSandBoxMode ? this.isSandBoxMode : this.balance?.balance <= 0;
  }

  getBalance(): void {
    const request$ = this.balanceService.balance();

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => this.balance = res.data,
      error: (err) => this.showError(err)
    });
  }

  getBankList(): void {
    const request$ = this.bankService.bankList();

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => this.bankList = res.data,
      error: (err) => this.showError(err)
    });
  }

  handleTopUp(): void {
    if (this.isSandBoxMode) return;
    this.baseService.getData(CHECK_ACTIVE + '?type=balance')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ResponseCustomType<ActiveBillResponse>) => {
          const { data } = res;
          const isExpired = new Date(data.qr_expire_date) < new Date();

          if (isExpired && data.have_active_bill && data.qr_xendit_id) {
            this.handleCancel(data.qr_xendit_id);
            return;
          }

          if (!data.have_active_bill) {
            this.modalService.open(ModalTopUpSaldoComponent, { backdrop: 'static', centered: true, size: 'md', keyboard: false });
            return;
          }

          const modal = this.modalService.open(ModalCheckPaymentComponent, { backdrop: 'static', centered: true, size: 'md', keyboard: false });
          const inst = modal.componentInstance;
          inst.invoiceID = data.invoice_xendit_id || data.qr_xendit_id;
          inst.allowQris = data.allow_qr;
          inst.expiredAt = data.qr_expire_date;
          inst.urlPayment = data.invoice_xendit_url;
          inst.qrCode = data.qr_xendit_qrstring;
          inst.isQris = !data.invoice_xendit_id;
          inst.totalPrice = data.qr_amount;
        },
        error: (err) => this.showError(err)
      });
  }

  handleCancel(invoiceId: string): void {
    this.baseService.putData(CANCEL_INVOICE, { invoice_id: invoiceId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.modalService.open(ModalTopUpSaldoComponent, { backdrop: 'static', centered: true, size: 'md', keyboard: false }),
        error: (err) => this.showError(err)
      });
  }

  handleTarikSaldo(type: string): void {
    const filter = {
      start_date: '2019-01-01',
      end_date: moment().format('YYYY-MM-DD'),
      type: type,
    };

    this.balanceService.withdrawlList(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const hasBlockedRequest = res.data?.some((item: wdListModel) =>
            (item.type === 'balance' && item.status === 'request') || item.status === 'review'
          );

          this.modalService.open(hasBlockedRequest ? BlokReqWDComponent : ModalTarikSaldoComponent, {
            backdrop: 'static',
            centered: true,
          });
        },
        error: (err) => this.showError(err)
      });
  }

  handleWithdrawal(): void {
    if (this.isSandBoxMode) return;
    if (!this.pinStatus) {
      this.handleNoPIN();
    } else if (!this.bankList || this.bankList.length === 0) {
      this.handleNoRek();
    } else {
      this.handleTarikSaldo('balance');
    }
  }

  handleNoRek(): void {
    this.modalService.open(BuatRekeningComponent, { backdrop: 'static', centered: true });
  }

  handleNoPIN(): void {
    this.modalService.open(BuatPinComponent, { backdrop: 'static', centered: true });
  }

  maintenance(): void {
    this.modalService.open(MaintenanceComponent, { backdrop: 'static', centered: true });
  }

  private showError(err: HttpErrorResponse): void {
    this.message.create('error', err.error?.data?.errors || 'An error occurred');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
